import React, { useMemo } from 'react'
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react'
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isToday,
} from 'date-fns'
import moment from 'moment'
import { getShiftBadgeClassByIndex, getShiftLetter } from './shiftLabelUtils'

const WEEKDAY_LABELS = {
    ja: ['月', '火', '水', '木', '金', '土', '日'],
    en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
}

function ShiftMonthCalendar({
    currentDate,
    staffshift,
    lang,
    onMonthChange,
    formatMonthLabel,
}) {
    const weekdayLabels = lang === 'en' ? WEEKDAY_LABELS.en : WEEKDAY_LABELS.ja

    const monthWeeks = useMemo(() => {
        const monthStart = startOfMonth(currentDate)
        const monthEnd = endOfMonth(currentDate)
        const calStart = startOfWeek(monthStart, { weekStartsOn: 1 })
        const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
        const allDays = eachDayOfInterval({ start: calStart, end: calEnd })

        const weeks = []
        for (let i = 0; i < allDays.length; i += 7) {
            weeks.push(allDays.slice(i, i + 7))
        }
        return weeks
    }, [currentDate])

    const getDayEntries = (day) => {
        const dateKey = moment(day).format('YYYY-MM-DD')
        return staffshift
            .map((staff) => {
                const shifts = staff?.shiftData?.[dateKey]
                if (!shifts || !shifts.some((s) => s !== '')) return null
                const activeShifts = shifts
                    .map((name, index) => ({ name, index }))
                    .filter(({ name }) => name !== '')
                return { staff, shifts: activeShifts }
            })
            .filter(Boolean)
    }

    const renderDayShifts = (day) => {
        const entries = getDayEntries(day)
        if (entries.length === 0) return null

        return (
            <div className="shift-month-calendar__entries">
                {entries.map(({ staff, shifts }) => (
                    <div
                        key={staff?._id?.staffId || staff?.name}
                        className="shift-month-calendar__staff-entry"
                    >
                        <span className="shift-month-calendar__staff-name">{staff.name}</span>
                        <span className="shift-month-calendar__shift-badges">
                            {shifts.map(({ name, index }) => (
                                <span
                                    key={index}
                                    className={`shift_badge ${getShiftBadgeClassByIndex(index)}`}
                                >
                                    {getShiftLetter(name)}
                                </span>
                            ))}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className="shift-month-calendar">
            <div className="shift-month-calendar__nav">
                <a role="button" className="lh1 flex_item_cc" onClick={() => onMonthChange(-1)}>
                    <ArrowLeft2 variant="Bold" color="#0D0B0B" size="24" />
                </a>
                <h6>{formatMonthLabel()}</h6>
                <a role="button" className="lh1 flex_item_cc" onClick={() => onMonthChange(1)}>
                    <ArrowRight2 variant="Bold" color="#0D0B0B" size="24" />
                </a>
            </div>

            <div className="shift-month-calendar__weekdays">
                {weekdayLabels.map((label) => (
                    <div key={label} className="shift-month-calendar__weekday">
                        {label}
                    </div>
                ))}
            </div>

            {monthWeeks.map((week, weekIndex) => (
                <div key={weekIndex} className="shift-month-calendar__week">
                    {week.map((day) => {
                        const inMonth = isSameMonth(day, currentDate)
                        const today = isToday(day)

                        return (
                            <div
                                key={day.toISOString()}
                                className={[
                                    'shift-month-calendar__day',
                                    !inMonth && 'is-outside',
                                    today && 'is-today',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                <div className="shift-month-calendar__day-num">
                                    {format(day, 'd')}
                                </div>
                                {inMonth && renderDayShifts(day)}
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}

export default ShiftMonthCalendar
